// The share price module (RL-2541): one design shared by the Investor Centre
// and the relait-widgets embed. This is a copy of relait-widgets
// src/utils/sharePrice.js; keep the two identical so both surfaces show the
// same order, labels, formats and chart settings. chartAccent is left out:
// the Investor Centre has no data-chart-color to weigh against the Primary.

type Numeric = number | string | null | undefined

export interface ShareMarketData {
  open_price?: Numeric
  previous_close?: Numeric
  volume?: Numeric
  vwap?: Numeric
  bid?: Numeric
  ask?: Numeric
  market_cap?: Numeric
  shares_on_issue?: Numeric
  shares_outstanding?: Numeric
  low?: Numeric
  high?: Numeric
  _52w_low?: Numeric
  _52w_high?: Numeric
  current_price?: Numeric
}

type Data = ShareMarketData | null | undefined

// Used whenever the company has no Primary colour or it cannot be read. Same
// default the backend serves from /theme.
export const DEFAULT_ACCENT = '#07004D'

const MUTED = '#5B6270'
const GRID_LINE = 'rgba(20, 23, 31, 0.06)'
const VOLUME = 'rgba(91, 98, 112, 0.2)'

// TradingView draws inside its own iframe, so a web font loaded by the host
// page never reaches it. This is the stack the chart has always used.
const SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif'

const GENERIC_FONTS = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'inherit',
  'initial'
])

const toNumber = (value: Numeric): number | null => {
  if (value === null || value === undefined || value === '') return null
  const number = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(number) ? number : null
}

export const formatDecimalValue = (value: Numeric, decimals = 3) => {
  const number = toNumber(value)
  return number === null ? 'N/A' : number.toFixed(decimals)
}

export const formatCompact = (value: Numeric) => {
  const number = toNumber(value)
  if (number === null) return 'N/A'
  if (number >= 1_000_000_000_000) return `${(number / 1_000_000_000_000).toFixed(3)}T`
  if (number >= 1_000_000_000) return `${(number / 1_000_000_000).toFixed(3)}B`
  if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(3)}M`
  if (number >= 1_000) return `${(number / 1_000).toFixed(3)}K`
  return number.toFixed(3)
}

export const formatNumberInMillion = (value: Numeric) => {
  const number = toNumber(value)
  if (number === null) return 'N/A'
  return `${(number / 1_000_000).toFixed(3)}M`
}

export const formatCurrency = (value: Numeric) => {
  const formatted = formatCompact(value)
  return formatted === 'N/A' ? 'N/A' : `$${formatted}`
}

// One stat per cell, in display order. Formats are the ones the widget has
// always used; only the labels changed (sentence case).
export const STAT_CELLS: { label: string; value: (data: Data) => string }[] = [
  { label: 'Open', value: data => formatDecimalValue(data?.open_price) },
  { label: 'Previous close', value: data => formatDecimalValue(data?.previous_close) },
  { label: 'Volume', value: data => formatNumberInMillion(data?.volume) },
  { label: 'VWAP (day)', value: data => formatCurrency(data?.vwap) },
  { label: 'Bid', value: data => formatDecimalValue(data?.bid) },
  { label: 'Ask', value: data => formatDecimalValue(data?.ask) },
  { label: 'Market cap', value: data => formatCurrency(data?.market_cap) },
  {
    label: 'Shares on issue',
    value: data => formatCompact(data?.shares_on_issue ?? data?.shares_outstanding)
  }
]

export const RANGE_CELLS: {
  label: string
  low: (data: Data) => Numeric
  high: (data: Data) => Numeric
}[] = [
  { label: 'Day range', low: data => data?.low, high: data => data?.high },
  { label: '52-week range', low: data => data?._52w_low, high: data => data?._52w_high }
]

// The last traded price, or null. The price feed stores 0 when it has no last
// price, and a share never trades at 0, so 0 means "unknown" here.
export const lastPrice = (data: Data) => {
  const price = toNumber(data?.current_price)
  return price !== null && price > 0 ? price : null
}

// Where the last price sits between low and high, from 0 to 1, or null when it
// cannot be placed. The 52-week figures refresh on a different schedule from
// the price, so a price just outside the range is pinned to the nearer end.
export const rangePosition = (low: Numeric, high: Numeric, last: Numeric) => {
  const lo = toNumber(low)
  const hi = toNumber(high)
  const price = toNumber(last)
  if (lo === null || hi === null || price === null || price <= 0 || hi <= lo) {
    return null
  }
  return Math.min(1, Math.max(0, (price - lo) / (hi - lo)))
}

// [r, g, b] for a hex (#rgb, #rgba, #rrggbb, #rrggbbaa) or rgb()/rgba() colour,
// or null. Any alpha is dropped: the accent is always drawn opaque.
const parseColor = (value: unknown): number[] | null => {
  if (typeof value !== 'string') return null
  const color = value.trim()

  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(color)
  if (hex) {
    let digits = hex[1]
    if (digits.length <= 4) {
      digits = digits
        .split('')
        .map(digit => digit + digit)
        .join('')
    }
    return [0, 2, 4].map(start => parseInt(digits.slice(start, start + 2), 16))
  }

  const rgb =
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+%?\s*)?\)$/i.exec(color)
  if (rgb) {
    const channels = rgb.slice(1, 4).map(Number)
    return channels.every(channel => channel <= 255) ? channels : null
  }

  return null
}

// Every colour the module derives from the chart colour.
export const accentPalette = (accent: unknown) => {
  const [r, g, b] = parseColor(accent) || (parseColor(DEFAULT_ACCENT) as number[])
  const rgba = (alpha: number) => `rgba(${r}, ${g}, ${b}, ${alpha})`
  return {
    line: rgba(1),
    fillTop: rgba(0.16),
    fillBottom: rgba(0),
    tint: rgba(0.12)
  }
}

// The host page's own font first, so the chart matches the page wherever the
// viewer has that font installed, then the stack the chart has always used.
export const chartFontFamily = (element: Element | null) => {
  if (!element || typeof window === 'undefined') return SYSTEM_FONT_STACK
  const family = window.getComputedStyle(element).fontFamily || ''
  const first = family
    .split(',')[0]
    .trim()
    .replace(/^["']|["']$/g, '')
  if (!/^[A-Za-z][\w -]*$/.test(first) || GENERIC_FONTS.has(first.toLowerCase())) {
    return SYSTEM_FONT_STACK
  }
  return `${first}, ${SYSTEM_FONT_STACK}`
}

// Options for TradingView's symbol overview embed. TradingView keeps drawing
// the header, the price change (with its red/green), the market status, the
// range tabs and the hover crosshair; these set their colours and font only.
export const buildSymbolOverviewConfig = ({
  symbol,
  accent,
  fontFamily = SYSTEM_FONT_STACK,
  colorTheme = 'light',
  transparent = true
}: {
  symbol: string
  accent: unknown
  fontFamily?: string
  colorTheme?: string
  transparent?: boolean
}) => {
  const colors = accentPalette(accent)
  return {
    symbols: [[symbol]],
    chartOnly: false,
    width: '100%',
    height: '100%',
    locale: 'en',
    colorTheme,
    autosize: true,
    isTransparent: transparent,
    showVolume: true,
    showMA: false,
    hideDateRanges: false,
    hideMarketStatus: false,
    hideSymbolLogo: true,
    scalePosition: 'right',
    scaleMode: 'Normal',
    fontFamily,
    fontSize: '10',
    fontColor: MUTED,
    gridLineColor: GRID_LINE,
    noTimeScale: false,
    valuesTracking: '1',
    changeMode: 'price-and-percent',
    chartType: 'area',
    headerFontSize: 'medium',
    lineWidth: 2,
    lineType: 0,
    lineColor: colors.line,
    topColor: colors.fillTop,
    bottomColor: colors.fillBottom,
    belowLineFillColorGrowing: colors.fillTop,
    belowLineFillColorFalling: colors.fillTop,
    belowLineFillColorGrowingBottom: colors.fillBottom,
    belowLineFillColorFallingBottom: colors.fillBottom,
    // TradingView colours the hover values with these. Left unset they turn
    // red or green with the period's change, which reads as a judgement on
    // the stock, so they follow the line instead.
    upColor: colors.line,
    downColor: colors.line,
    borderUpColor: colors.line,
    borderDownColor: colors.line,
    wickUpColor: colors.line,
    wickDownColor: colors.line,
    volumeUpColor: VOLUME,
    volumeDownColor: VOLUME,
    dateRanges: ['1d|1', '1m|30', '3m|60', '12m|1D', '60m|1W', 'all|1M']
  }
}
